import React, { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api$/, '');
const storageUrl = path => (!path ? null : path.startsWith('http') ? path : `${STORAGE_BASE}${path}`);

const STATUS_COLOR = {
  available: '#22C55E',
  held:      '#F59E0B',
  sold:      '#EF4444',
  blocked:   '#9CA3AF',
  disabled:  '#D1D5DB',
};

const STATUS_LABEL = {
  available: 'Available',
  held:      'Held',
  sold:      'Sold',
  blocked:   'Blocked',
  disabled:  'Disabled',
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function svgPoint(e, svgRef, zoom, pan) {
  const rect = svgRef.current.getBoundingClientRect();
  return {
    x: (e.clientX - rect.left - pan.x) / zoom,
    y: (e.clientY - rect.top  - pan.y) / zoom,
  };
}

function inferCustomerEditorDims(section) {
  if (section.shape_data?._editorW) return { editorW: section.shape_data._editorW, editorH: section.shape_data._editorH };
  const seats = section.seats || [];
  if (!seats.length) return { editorW: section.width, editorH: section.height };
  const maxX = Math.max(...seats.map(s => s.x));
  const maxY = Math.max(...seats.map(s => s.y));
  return {
    editorW: Math.max(section.width,  maxX + 24),
    editorH: Math.max(section.height, maxY + 24),
  };
}

function seatDisplayRadius(section) {
  const sp = section.shape_data?._seat_spacing;
  if (sp && sp > 0) return clamp(sp * 0.42, 3, 9);
  const seats = section.seats || [];
  const n = seats.length;
  if (!n) return 9;
  const { editorW, editorH } = inferCustomerEditorDims(section);
  return clamp(Math.sqrt((editorW * editorH) / n) * 0.40, 3, 9);
}

/**
 * CustomerSeatMap — 2-level view
 *
 * Map view : sections shown as shapes with seat-count badges; click a reserved
 *            section to zoom in and see individual seats.
 * Detail view : the clicked section fills the canvas; user clicks seats to select.
 *
 * Props:
 *   seatMap       – full map object from GET /events/:id/seat-map
 *   selectedSeats – array of seat objects the user has chosen
 *   onSeatToggle  – fn(seat) called when user clicks an available seat
 *   gaSelections  – { [sectionId]: qty } for GA sections
 *   onGaChange    – fn(sectionId, qty)
 */
export default function CustomerSeatMap({
  seatMap,
  selectedSeats = [],
  onSeatToggle,
  gaSelections = {},
  onGaChange,
  viewOnly = false,
}) {
  const [zoom, setZoom]           = useState(1);
  const [pan,  setPan]            = useState({ x: 20, y: 20 });
  const [viewMode, setViewMode]   = useState('map');     // 'map' | 'detail'
  const [detailSec, setDetailSec] = useState(null);
  const [savedView, setSavedView] = useState(null);      // { zoom, pan }
  const [transitioning, setTransitioning] = useState(false);
  const [tooltip, setTooltip]     = useState(null);

  const panRef       = useRef(null);
  const pinchRef     = useRef(null);  // tracks active pinch-to-zoom gesture
  const svgRef       = useRef(null);
  const zoomFitDone  = useRef(false); // only zoom-to-fit once; subsequent seatMap updates are status patches
  // Refs so touch handlers always read current values without stale closures
  const zoomRef      = useRef(1);
  const panXRef      = useRef(20);
  const panYRef      = useRef(20);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panXRef.current = pan.x; panYRef.current = pan.y; }, [pan]);

  const selectedIds = new Set(selectedSeats.map(s => s.id));

  // ── zoom-to-fit on first load ──────────────────────────────────────────────
  useEffect(() => {
    if (!seatMap || !svgRef.current || zoomFitDone.current) return;
    zoomFitDone.current = true;
    const r = svgRef.current.getBoundingClientRect();
    const PAD = 32;
    const z = clamp(
      Math.min((r.width - PAD * 2) / seatMap.width, (r.height - PAD * 2) / seatMap.height),
      0.15, 3
    );
    setZoom(z);
    setPan({ x: (r.width  - seatMap.width  * z) / 2, y: (r.height - seatMap.height * z) / 2 });
  }, [seatMap]);

  // ── scroll zoom ────────────────────────────────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const el = svgRef.current;
    if (!el) return;
    const r  = el.getBoundingClientRect();
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    setZoom(z => {
      const nz = clamp(z * factor, 0.15, 8);
      setPan(p => ({ x: mx - (mx - p.x) * (nz / z), y: my - (my - p.y) * (nz / z) }));
      return nz;
    });
  }, []);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ── touch pan + pinch zoom ─────────────────────────────────────────────────
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;

    const dist = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        const t = e.touches[0];
        panRef.current  = { startX: t.clientX - panXRef.current, startY: t.clientY - panYRef.current };
        pinchRef.current = null;
      } else if (e.touches.length === 2) {
        pinchRef.current = {
          dist:  dist(e.touches[0], e.touches[1]),
          zoom:  zoomRef.current,
          panX:  panXRef.current,
          panY:  panYRef.current,
          midX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          midY: (e.touches[0].clientY + e.touches[1].clientY) / 2,
        };
        panRef.current = null;
      }
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 1 && panRef.current && !pinchRef.current) {
        const t = e.touches[0];
        setPan({ x: t.clientX - panRef.current.startX, y: t.clientY - panRef.current.startY });
      } else if (e.touches.length === 2 && pinchRef.current) {
        const newDist  = dist(e.touches[0], e.touches[1]);
        const scale    = newDist / pinchRef.current.dist;
        const newZoom  = clamp(pinchRef.current.zoom * scale, 0.15, 8);
        const rect     = el.getBoundingClientRect();
        const midX     = pinchRef.current.midX - rect.left;
        const midY     = pinchRef.current.midY - rect.top;
        const ratio    = newZoom / pinchRef.current.zoom;
        setZoom(newZoom);
        setPan({
          x: midX - (midX - pinchRef.current.panX) * ratio,
          y: midY - (midY - pinchRef.current.panY) * ratio,
        });
      }
    };

    const onTouchEnd = () => {
      panRef.current   = null;
      pinchRef.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd);
    el.addEventListener('touchcancel',onTouchEnd);
    return () => {
      el.removeEventListener('touchstart',  onTouchStart);
      el.removeEventListener('touchmove',   onTouchMove);
      el.removeEventListener('touchend',    onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []); // runs once — reads live values through refs

  // ── pan ───────────────────────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    panRef.current = { startX: e.clientX - pan.x, startY: e.clientY - pan.y };
  };
  const handleMouseMove = (e) => {
    if (!panRef.current) return;
    setPan({ x: e.clientX - panRef.current.startX, y: e.clientY - panRef.current.startY });
  };
  const handleMouseUp = () => { panRef.current = null; };

  // ── 2-level navigation ────────────────────────────────────────────────────
  function enterDetailView(sec) {
    if (!sec?.has_seats || sec.shape_data?.isObject) return;
    const el = svgRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const PAD = 80;
    const { editorW, editorH } = inferCustomerEditorDims(sec);
    const targetZoom = clamp(
      Math.min((r.width - PAD * 2) / editorW, (r.height - PAD * 2) / editorH),
      0.05, 8
    );
    const targetPan = {
      x: r.width  / 2 - (sec.position_x + editorW / 2) * targetZoom,
      y: r.height / 2 - (sec.position_y + editorH / 2) * targetZoom,
    };
    setSavedView({ zoom, pan });
    setTransitioning(true);
    setTimeout(() => setTransitioning(false), 450);
    setDetailSec(sec);
    setViewMode('detail');
    setZoom(targetZoom);
    setPan(targetPan);
  }

  function exitDetailView() {
    setTransitioning(true);
    setTimeout(() => setTransitioning(false), 450);
    setViewMode('map');
    setDetailSec(null);
    if (savedView) {
      setZoom(savedView.zoom);
      setPan(savedView.pan);
      setSavedView(null);
    }
  }

  // Escape key exits detail view
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape' && viewMode === 'detail') exitDetailView(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [viewMode, savedView]);

  // ── seat click ────────────────────────────────────────────────────────────
  const handleSeatClick = (e, seat) => {
    e.stopPropagation();
    if (viewOnly) return;
    const isSelected = selectedSeats?.some(s => s.id === seat.id);
    // Allow deselecting an already-selected seat even if status is now 'held'
    if (seat.status !== 'available' && !isSelected) return;
    onSeatToggle?.(seat);
  };

  // ── render ────────────────────────────────────────────────────────────────
  const renderSection = (section) => {
    const { id, shape_type, shape_data, position_x: px, position_y: py,
            width: w, height: h, color, rotation = 0 } = section;
    const cx = px + w / 2, cy = py + h / 2;
    const isActive  = viewMode === 'detail' && String(id) === String(detailSec?.id);
    const isDimmed  = viewMode === 'detail' && !isActive;
    const isObj     = shape_data?.isObject;

    const transform = rotation
      ? `rotate(${rotation} ${cx} ${cy}) translate(${px} ${py})`
      : `translate(${px} ${py})`;

    // In detail mode the editor workspace may be larger than the visual section.
    const { editorW, editorH } = isActive ? inferCustomerEditorDims(section) : { editorW: w, editorH: h };
    const scaleX = editorW / w, scaleY = editorH / h;

    // ── shape ───────────────────────────────────────────────────────────────
    let sectionShape;
    if (shape_type === 'circle') {
      sectionShape = (
        <ellipse cx={editorW/2} cy={editorH/2} rx={editorW/2} ry={editorH/2}
          fill={color} fillOpacity={isDimmed ? 0.08 : 0.15}
          stroke={color} strokeWidth={isDimmed ? 1 : 1.5} />
      );
    } else if (shape_type === 'polygon' && shape_data?.points?.length) {
      const pts = shape_data.points.map(p => `${(p.x - px) * scaleX},${(p.y - py) * scaleY}`).join(' ');
      sectionShape = (
        <polygon points={pts} fill={color} fillOpacity={isDimmed ? 0.08 : 0.15}
          stroke={color} strokeWidth={isDimmed ? 1 : 1.5} />
      );
    } else {
      sectionShape = (
        <rect x={0} y={0} width={editorW} height={editorH} rx={4}
          fill={color} fillOpacity={isDimmed ? 0.08 : 0.15}
          stroke={color} strokeWidth={isDimmed ? 1 : 1.5} />
      );
    }

    // ── venue object (stage, entrance, etc.) ────────────────────────────────
    if (isObj) {
      return (
        <g key={id} transform={transform} opacity={isDimmed ? 0.2 : 1} pointerEvents="none">
          {sectionShape}
          <text x={w / 2} y={h / 2 + 4} textAnchor="middle" fontSize={11}
            fill="#111827" fontWeight="700" style={{ userSelect: 'none' }}>
            {section.name}
          </text>
        </g>
      );
    }

    // ── dimmed ghost (detail mode, not active) ───────────────────────────────
    if (isDimmed) {
      return (
        <g key={id} transform={transform} opacity={0.2} pointerEvents="none">
          {sectionShape}
          <text x={8} y={16} fontSize={10} fill={color} fontWeight="700"
            style={{ userSelect: 'none' }}>{section.name}</text>
        </g>
      );
    }

    // ── GA section ───────────────────────────────────────────────────────────
    if (!section.has_seats) {
      const remaining = section.remaining_capacity ?? section.capacity ?? 0;
      const soldOutGA = remaining === 0;
      const selected  = gaSelections[id] || 0;
      return (
        <g key={id} transform={transform}
          opacity={soldOutGA ? 0.35 : 1}
          style={{ cursor: viewOnly || soldOutGA ? 'default' : 'pointer' }}
          pointerEvents={soldOutGA ? 'none' : undefined}
          onClick={e => { e.stopPropagation(); if (!viewOnly) onGaChange?.(id, Math.min(remaining, selected + 1)); }}>
          {sectionShape}

          {/* Section name */}
          <text x={w / 2} y={h / 2 - (section.price_zone ? 6 : 4)} textAnchor="middle"
            fontSize={11} fill={color} fontWeight="700"
            style={{ userSelect: 'none' }} pointerEvents="none">
            {section.name}
          </text>

          {/* Price */}
          {section.price_zone && (
            <text x={w / 2} y={h / 2 + 9} textAnchor="middle"
              fontSize={9} fill={color} fillOpacity={0.75}
              style={{ userSelect: 'none' }} pointerEvents="none">
              QAR {section.price_zone.price}
            </text>
          )}

          {/* Hint */}
          <text x={w / 2} y={h - 6} textAnchor="middle" fontSize={8}
            fill={color} fillOpacity={0.5}
            style={{ userSelect: 'none' }} pointerEvents="none">
            {viewOnly ? `${remaining} spots left` : 'Tap to add'}
          </text>

          {/* Selected-count badge (purchase mode only) */}
          {!viewOnly && selected > 0 && (
            <g pointerEvents="none">
              <rect x={w - 30} y={4} width={26} height={16} rx={8} fill="#8B5CF6" />
              <text x={w - 17} y={15} textAnchor="middle" fontSize={9}
                fill="#78350F" fontWeight="700">{selected} ✓</text>
            </g>
          )}
        </g>
      );
    }

    // ── reserved-seating section (map view) ──────────────────────────────────
    const seats     = section.seats || [];
    const seatCount = seats.length;
    const availCount = seats.filter(s => s.status === 'available').length;
    const selCount   = seats.filter(s => selectedIds.has(s.id)).length;

    if (viewMode === 'map') {
      const soldOut = availCount === 0 && seatCount > 0;
      return (
        <g key={id} transform={transform}
          opacity={soldOut ? 0.35 : 1}
          style={{ cursor: soldOut ? 'default' : 'pointer' }}
          pointerEvents={soldOut ? 'none' : undefined}
          onClick={e => { e.stopPropagation(); enterDetailView(section); }}>
          {sectionShape}

          {/* Section name */}
          <text x={8} y={16} fontSize={11} fill={color} fontWeight="700"
            style={{ userSelect: 'none' }} pointerEvents="none">
            {section.name}
          </text>

          {/* Price */}
          {section.default_price && (
            <text x={8} y={28} fontSize={9} fill={color} fillOpacity={0.7}
              style={{ userSelect: 'none' }} pointerEvents="none">
              QAR {section.default_price}
            </text>
          )}

          {/* Selected seats indicator */}
          {selCount > 0 && (
            <g pointerEvents="none">
              <rect x={w - 32} y={4} width={28} height={16} rx={8} fill="#8B5CF6" />
              <text x={w - 18} y={15} textAnchor="middle" fontSize={9}
                fill="#78350F" fontWeight="700">{selCount} ✓</text>
            </g>
          )}

          {/* Hint at bottom */}
          {seatCount > 0 && (
            <text x={w/2} y={h - 6} textAnchor="middle" fontSize={8}
              fill={color} fillOpacity={0.55} style={{ userSelect: 'none' }} pointerEvents="none">
              {viewOnly ? `${availCount} available` : 'Click to choose seats'}
            </text>
          )}
        </g>
      );
    }

    // ── reserved-seating section (detail view — active) ──────────────────────
    const r = seatDisplayRadius(section);
    const labelFs = Math.max(4, Math.min(7, r * 0.7));

    return (
      <g key={id} transform={transform}>
        {sectionShape}

        {/* Pill label floated ABOVE the section top edge */}
        <g pointerEvents="none">
          <rect x={editorW/2 - 52} y={-24} width={104} height={18} rx={9}
            fill={color} fillOpacity={0.92} />
          <text x={editorW/2} y={-11} textAnchor="middle" fontSize={10} fill="white" fontWeight="700"
            style={{ userSelect: 'none' }}>
            {section.name}{section.default_price ? `  ·  QAR ${section.default_price}` : ''}
          </text>
        </g>

        {/* Individual seats */}
        {seats.map(seat => {
          const isSelected  = selectedIds.has(seat.id);
          const isAvailable = seat.status === 'available';
          const seatColor   = isSelected ? '#8B5CF6' : (STATUS_COLOR[seat.status] || '#9CA3AF');
          const seatRot     = seat.rotation || 0;

          return (
            <g key={seat.id}
              transform={seatRot ? `rotate(${seatRot},${seat.x},${seat.y})` : undefined}
              style={{ cursor: viewOnly ? 'default' : isAvailable ? 'pointer' : 'not-allowed' }}
              onClick={e => handleSeatClick(e, seat)}
              onMouseEnter={e => setTooltip({
                x: e.clientX, y: e.clientY,
                label: `${seat.label || seat.seat_number}${seat.is_handicapped ? ' ♿' : ''} — ${STATUS_LABEL[seat.status]}${isAvailable && seat.price ? ` — QAR ${seat.price}` : ''}`,
              })}
              onMouseLeave={() => setTooltip(null)}>
              <circle cx={seat.x} cy={seat.y} r={r}
                fill={seatColor}
                fillOpacity={isAvailable || isSelected ? 1 : 0.55}
                stroke={seat.is_handicapped ? '#2563EB' : (isSelected ? '#92400E' : '#fff')}
                strokeWidth={isSelected ? Math.max(1, r * 0.2) : Math.max(0.4, r * 0.08)} />
              {seat.is_handicapped ? (
                <text x={seat.x} y={seat.y + r * 0.38} textAnchor="middle"
                  fontSize={Math.max(4, r * 0.8)} fill="#fff"
                  pointerEvents="none" style={{ userSelect: 'none' }}>♿</text>
              ) : r >= 5 && (
                <text x={seat.x} y={seat.y + r * 0.38} textAnchor="middle"
                  fontSize={labelFs} fontWeight="700"
                  fill={isSelected ? '#92400E' : '#fff'}
                  pointerEvents="none" style={{ userSelect: 'none' }}>
                  {seat.label || seat.seat_number}
                </text>
              )}
            </g>
          );
        })}
      </g>
    );
  };

  if (!seatMap) return null;

  const sections = seatMap.sections || [];
  const bgUrl    = storageUrl(seatMap.background_image_url || seatMap.background_image);

  return (
    <div className="relative w-full" style={{ height: 520, display: 'flex', flexDirection: 'column' }}>

      {/* Detail-view hint bar */}
      {viewMode === 'detail' && detailSec && (
        <div className="d-flex align-items-center gap-3 px-3 py-1 border-bottom flex-shrink-0"
          style={{ background: viewOnly ? '#EFF6FF' : '#F0FDF4', fontSize: 12, color: viewOnly ? '#1e40af' : '#166534', zIndex: 10 }}>
          <span>
            <strong>{detailSec.name}</strong>
            {viewOnly
              ? <>&nbsp;·&nbsp; Viewing seat availability &nbsp;·&nbsp; <strong>Esc</strong> to go back</>
              : <>&nbsp;·&nbsp; Click an available seat to select it &nbsp;·&nbsp; <strong>Esc</strong> to go back</>
            }
          </span>
          <button
            className="ms-auto btn btn-sm"
            style={{ background: '#16a34a', color: 'white', fontSize: 11, padding: '2px 14px', borderRadius: 20, border: 'none' }}
            onClick={exitDetailView}>
            ← Back to Map
          </button>
        </div>
      )}

      <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        {/* Controls */}
        <div className="absolute top-2 right-2 z-10 flex gap-1.5" style={{ zIndex: 10 }}>
          {viewMode === 'detail' && (
            <button onClick={exitDetailView}
              className="h-8 bg-green-600 text-white shadow rounded text-xs px-3 hover:bg-green-700 flex items-center font-medium">
              ← Map
            </button>
          )}
          <button onClick={() => setZoom(z => clamp(z + 0.15, 0.15, 8))}
            className="w-8 h-8 bg-white shadow rounded text-gray-700 hover:bg-gray-50 font-bold text-lg flex items-center justify-center">+</button>
          <button onClick={() => setZoom(z => clamp(z - 0.15, 0.15, 8))}
            className="w-8 h-8 bg-white shadow rounded text-gray-700 hover:bg-gray-50 font-bold text-lg flex items-center justify-center">−</button>
          <button onClick={() => {
            if (viewMode === 'detail') { exitDetailView(); }
            else {
              const el = svgRef.current;
              if (!el) return;
              const r2 = el.getBoundingClientRect();
              const PAD = 32;
              const z = clamp(Math.min((r2.width - PAD * 2) / seatMap.width, (r2.height - PAD * 2) / seatMap.height), 0.15, 3);
              setZoom(z);
              setPan({ x: (r2.width - seatMap.width * z) / 2, y: (r2.height - seatMap.height * z) / 2 });
            }
          }}
            className="px-2 h-8 bg-white shadow rounded text-gray-700 text-xs hover:bg-gray-50">
            {viewMode === 'detail' ? 'Back' : 'Fit'}
          </button>
        </div>

        {/* Legend */}
        <div className="absolute bottom-2 left-2 z-10 bg-white shadow rounded-lg px-3 py-2 flex gap-3 flex-wrap" style={{ zIndex: 10 }}>
          {Object.entries(STATUS_COLOR)
            .filter(([k]) => ['available', 'held', 'sold', 'blocked'].includes(k))
            .map(([k, c]) => (
              <div key={k} className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ background: c }} />
                <span className="text-xs text-gray-600 capitalize">{STATUS_LABEL[k]}</span>
              </div>
            ))}
        </div>

        {/* SVG */}
        <svg ref={svgRef} width="100%" height="100%"
          className="bg-gray-100 rounded-xl border"
          style={{ display: 'block', cursor: panRef.current ? 'grabbing' : 'grab' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}>

          <g style={{
            transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            transition: transitioning ? 'transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94)' : 'none',
          }}>
            {/* Canvas background */}
            <rect x={0} y={0} width={seatMap.width} height={seatMap.height}
              fill="white" stroke="#E2E8F0" strokeWidth="1" rx="8" />

            {/* Background image */}
            {bgUrl && (
              <image href={bgUrl}
                x={seatMap.bg_x ?? 0} y={seatMap.bg_y ?? 0}
                width={seatMap.bg_width ?? seatMap.width}
                height={seatMap.bg_height ?? seatMap.height}
                preserveAspectRatio="xMidYMid meet"
                opacity={seatMap.bg_opacity ?? 0.3} />
            )}

            {/* Grid */}
            <defs>
              <pattern id="custGrid" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#F1F5F9" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect x={0} y={0} width={seatMap.width} height={seatMap.height} fill="url(#custGrid)" />

            {sections.map(renderSection)}
          </g>
        </svg>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div className="fixed z-50 pointer-events-none bg-gray-900 text-white text-xs rounded px-2 py-1 shadow-lg"
          style={{ top: tooltip.y - 36, left: tooltip.x + 8 }}>
          {tooltip.label}
        </div>
      )}
    </div>
  );
}
